/**
 * Redux container for AgentScannersView component
 *
 * This container connects the AgentScannersView presentational component to the Redux store
 * using React-Redux's connect() HOC with a custom RootStoreContext.
 *
 * @see AgentScannersView for the presentational component
 */
import { connect } from 'react-redux';
import RootStoreContext from '~/contexts/RootStoreContext';
import { actions as modalActions } from '~/domains/modal';
import {
  selectors as agentScannersSelectors,
  actions as agentScannersActions
} from '~/domains/agentScanners';
import {
  actions as commonActions,
  selectors as commonSelectors
} from '~/domains/common';
import { getActiveOrganizationId } from '~/utils/organization';
import AgentScannersView from './AgentScannersView';

/**
 * Maps Redux state to component props
 */
const mapStateToProps = (state) => {
  const profile = commonSelectors.profile(state);
  const activeOrganizationId = getActiveOrganizationId(profile);

  return {
    scanConfigurations: agentScannersSelectors.scanners(state),
    isFetchingScanConfigurations:
      agentScannersSelectors.isFetchingScanners(state),
    organizationId: activeOrganizationId,
    organizationName: profile?.organization?.name,
    ownerId: profile?.id,
    context: commonSelectors.context(state),
    agentCapacity: agentScannersSelectors.agentCapacity(state)
  };
};

/**
 * Maps Redux dispatch actions to component props
 */
const mapDispatchToProps = (dispatch) => ({
  openModal: ({ modalType, modalProps }) =>
    dispatch(modalActions.openModal({ modalType, modalProps })),

  /**
   * Creates a new scanner and shows success alert.
   * @param {string} organizationId - The organization ID
   * @param {Object} scannerData - Scanner data from the create form
   * @param {string} [scannerData.name] - Scanner name (validated with optional chaining + fallback)
   */
  onCreateScanner: (organizationId, scannerData) => {
    const startTime = Date.now();
    const MIN_DISPLAY_TIME = 3000; // 3 seconds minimum

    dispatch(
      agentScannersActions.createAgentScanner(organizationId, scannerData)
    ).then(() => {
      // Ensure alert is visible for at least MIN_DISPLAY_TIME
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, MIN_DISPLAY_TIME - elapsed);

      setTimeout(() => {
        // Reload the list after alert closes to give backend time to update
        // isHTML: false - no HTML needed, safe by default
        const addAlert = {
          type: 'success',
          message: `Agent scanner '${scannerData?.name || 'Unknown'}' was created.`,
          isHTML: false
        };

        dispatch(commonActions.setAlert(addAlert));
      }, remainingTime);
    });
  },

  /**
   * Updates an existing scanner and shows success alert.
   * @param {string} organizationId - The organization ID
   * @param {string} scannerId - The scanner ID to update
   * @param {Object} scannerData - Scanner data from the edit form
   * @param {string} [scannerData.name] - Scanner name (validated with optional chaining + fallback)
   */
  onUpdateScanner: (organizationId, scannerId, scannerData) => {
    const startTime = Date.now();
    const MIN_DISPLAY_TIME = 3000; // 3 seconds minimum

    dispatch(
      agentScannersActions.updateAgentScanner(
        organizationId,
        scannerId,
        scannerData
      )
    ).then(() => {
      // Ensure alert is visible for at least MIN_DISPLAY_TIME
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, MIN_DISPLAY_TIME - elapsed);

      setTimeout(() => {
        // Reload the list after alert closes to give backend time to update
        // isHTML: false - no HTML needed, safe by default
        const addAlert = {
          type: 'success',
          message: `Agent scanner '${scannerData?.name || 'Unknown'}' was updated.`,
          isHTML: false
        };

        dispatch(commonActions.setAlert(addAlert));
      }, remainingTime);
    });
  },
  onLoadScanners: (organizationId) =>
    dispatch(agentScannersActions.loadAgentScanners(organizationId)),
  onLoadAgentCapacity: (organizationId) =>
    dispatch(agentScannersActions.loadAgentCapacity(organizationId))
});

/**
 * Connected component with Redux state and actions
 * Uses custom RootStoreContext for store access
 */
export default connect(mapStateToProps, mapDispatchToProps, null, {
  context: RootStoreContext
})(AgentScannersView);
